/**
 * Stage Plot Editor — State management with undo/redo.
 *
 * Single-source schema (see shared/types/stageplot.ts):
 *   StageElement = { id, iconId, category, label, x, y, width, height,
 *                     rotation, color }
 * Positions x/y and sizes width/height are in METERS.
 */

import { useReducer, useCallback, useRef } from 'react';
import type { StagePlot, StageElement } from '../../shared/types/stageplot';
import { riserFootprint, type RiserConfig } from './riserConfig';
import { normalizeElementConfig, normalizePlotElementConfigs } from '../../lib/stageplotElements';

export type EditorAction =
  | { type: 'LOAD_PLOT'; plot: StagePlot }
  | { type: 'ADD_ELEMENT'; element: StageElement }
  | { type: 'INSERT_ELEMENT_BEFORE'; element: StageElement; beforeId: string }
  | { type: 'MOVE_ELEMENT'; id: string; x: number; y: number }
  | { type: 'MOVE_ELEMENTS'; deltas: { id: string; x: number; y: number }[] }
  | { type: 'RESIZE_ELEMENT'; id: string; width: number; height: number }
  | { type: 'ROTATE_ELEMENT'; id: string; rotation: number }
  | { type: 'UPDATE_ELEMENT'; id: string; patch: Partial<StageElement> }
  | { type: 'REORDER_ELEMENT'; id: string; place: 'front' | 'back' | 'forward' | 'backward' }
  | { type: 'DELETE_ELEMENT'; id: string }
  | { type: 'DUPLICATE_ELEMENT'; id: string }
  | { type: 'SET_STAGE'; width: number; height: number }
  | { type: 'SELECT_ELEMENT'; id: string | null }
  | { type: 'SELECT_ELEMENTS'; ids: string[] }
  | { type: 'UNDO' }
  | { type: 'REDO' };

export interface EditorState {
  plot: StagePlot;
  selectedId: string | null;
  selectedIds: string[];
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
}

interface HistoryFrame {
  plot: StagePlot;
  selectedId: string | null;
}

function touchPlot(plot: StagePlot, patch: Partial<StagePlot>): StagePlot {
  return {
    ...plot,
    ...patch,
    metadata: {
      ...plot.metadata,
      lastModified: Date.now(),
    },
  };
}

/** Keep a riser element's footprint in sync with its config (count × block). */
function withRiserFootprint(el: StageElement): StageElement {
  if (el.iconId === 'risers' && el.config) {
    const fp = riserFootprint(el.config as unknown as RiserConfig);
    if (fp) return { ...el, width: fp.width, height: fp.height };
  }
  return el;
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'LOAD_PLOT': {
      // Normalize composite configs on EVERY load (deserialize, generated
      // draft injection, undo/redo): any composite that lost its config is
      // reinstated so the renderer can't crash. This is the single-source
      // guard covering all load paths.
      const elements = normalizePlotElementConfigs(action.plot.elements);
      return { ...state, plot: { ...action.plot, elements }, isDirty: false };
    }

    case 'ADD_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, { elements: [...state.plot.elements, normalizeElementConfig(action.element)] }),
        selectedId: action.element.id,
        selectedIds: [action.element.id],
        isDirty: true,
      };

    case 'INSERT_ELEMENT_BEFORE': {
      // Insert an element directly before another in the array. Rendering z-order
      // is the array index (front = later index), so placing the riser BEFORE the
      // kit puts it BEHIND the kit on the stage. Fall back to appending if the
      // anchor (beforeId) is gone.
      const idx = state.plot.elements.findIndex((el) => el.id === action.beforeId);
      const els = [...state.plot.elements];
      const element = normalizeElementConfig(action.element);
      if (idx === -1) els.push(element);
      else els.splice(idx, 0, element);
      return {
        ...state,
        plot: touchPlot(state.plot, { elements: els }),
        selectedId: action.element.id,
        selectedIds: [action.element.id],
        isDirty: true,
      };
    }

    case 'MOVE_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.map((el) =>
            el.id === action.id ? withRiserFootprint({ ...el, x: action.x, y: action.y }) : el,
          ),
        }),
        isDirty: true,
      };

    case 'MOVE_ELEMENTS':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.map((el) => {
            const d = action.deltas.find((x) => x.id === el.id);
            return d ? withRiserFootprint({ ...el, x: d.x, y: d.y }) : el;
          }),
        }),
        isDirty: true,
      };

    case 'RESIZE_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.map((el) =>
            el.id === action.id ? { ...el, width: action.width, height: action.height } : el,
          ),
        }),
        isDirty: true,
      };

    case 'ROTATE_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.map((el) =>
            el.id === action.id ? { ...el, rotation: action.rotation } : el,
          ),
        }),
        isDirty: true,
      };

    case 'UPDATE_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.map((el) =>
            el.id === action.id ? withRiserFootprint({ ...el, ...action.patch }) : el,
          ),
        }),
        isDirty: true,
      };

    case 'REORDER_ELEMENT': {
      // Source of truth = position in the elements array (front = last).
      const els = [...state.plot.elements];
      const idx = els.findIndex((el) => el.id === action.id);
      if (idx === -1) return state;
      const [target] = els.splice(idx, 1);
      if (action.place === 'front') els.push(target);
      else if (action.place === 'back') els.unshift(target);
      else if (action.place === 'forward') els.splice(idx, 0, target); // one slot toward the end
      else els.splice(Math.max(0, idx - 1), 0, target); // one slot toward the start
      return {
        ...state,
        plot: touchPlot(state.plot, { elements: els }),
        isDirty: true,
      };
    }

    case 'DELETE_ELEMENT':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          elements: state.plot.elements.filter((el) => el.id !== action.id),
        }),
        selectedId: state.selectedId === action.id ? null : state.selectedId,
        selectedIds: state.selectedIds.filter((id) => id !== action.id),
        isDirty: true,
      };

    case 'DUPLICATE_ELEMENT': {
      const src = state.plot.elements.find((el) => el.id === action.id);
      if (!src) return state;
      const clone: StageElement = {
        ...src,
        id: `el-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`,
        x: Math.round((src.x + 0.5) * 100) / 100,
        y: Math.round((src.y + 0.5) * 100) / 100,
      };
      return {
        ...state,
        plot: touchPlot(state.plot, { elements: [...state.plot.elements, clone] }),
        selectedId: clone.id,
        selectedIds: [clone.id],
        isDirty: true,
      };
    }

    case 'SET_STAGE':
      return {
        ...state,
        plot: touchPlot(state.plot, {
          stageDimensions: { ...state.plot.stageDimensions, width: action.width, height: action.height },
        }),
        isDirty: true,
      };

    case 'SELECT_ELEMENT':
      return { ...state, selectedId: action.id, selectedIds: action.id ? [action.id] : [] };

    case 'SELECT_ELEMENTS':
      return {
        ...state,
        selectedId: action.ids.length === 1 ? action.ids[0] : action.ids[0] ?? null,
        selectedIds: action.ids,
      };

    default:
      return state;
  }
}

const MAX_HISTORY = 50;

export function useUndoableReducer(initial: StagePlot) {
  const past = useRef<HistoryFrame[]>([]);
  const future = useRef<HistoryFrame[]>([]);

  const [state, rawDispatch] = useReducer(editorReducer, {
    plot: initial,
    selectedId: null,
    selectedIds: [],
    canUndo: false,
    canRedo: false,
    isDirty: false,
  } satisfies EditorState);

  const frameFor = useCallback((s: EditorState): HistoryFrame => ({ plot: s.plot, selectedId: s.selectedId }), []);

  const dispatch = useCallback(
    (action: EditorAction) => {
      if (action.type === 'UNDO') {
        if (past.current.length === 0) return;
        const prev = past.current[past.current.length - 1];
        past.current = past.current.slice(0, -1);
        future.current = [{ plot: state.plot, selectedId: state.selectedId }, ...future.current];
        rawDispatch({ type: 'LOAD_PLOT', plot: prev.plot });
        rawDispatch({ type: 'SELECT_ELEMENT', id: prev.selectedId });
        return;
      }
      if (action.type === 'REDO') {
        if (future.current.length === 0) return;
        const next = future.current[0];
        future.current = future.current.slice(1);
        past.current = [...past.current, frameFor(state)];
        rawDispatch({ type: 'LOAD_PLOT', plot: next.plot });
        rawDispatch({ type: 'SELECT_ELEMENT', id: next.selectedId });
        return;
      }
      if (action.type === 'SELECT_ELEMENT' || action.type === 'SELECT_ELEMENTS') {
        rawDispatch(action);
        return;
      }
      // Mutating action — snapshot for undo (including the current selection).
      past.current = [...past.current.slice(-(MAX_HISTORY - 1)), frameFor(state)];
      future.current = [];
      rawDispatch(action);
    },
    [state, frameFor],
  );

  return {
    state: {
      ...state,
      canUndo: past.current.length > 0,
      canRedo: future.current.length > 0,
    } satisfies EditorState,
    dispatch,
    undo: () => dispatch({ type: 'UNDO' }),
    redo: () => dispatch({ type: 'REDO' }),
  };
}
